import { useState } from 'react';
import { ActivityTab } from './tabs/Activity';
import { FiltersTab } from './tabs/Filters';
import { SubscriptionsTab } from './tabs/Subscriptions';
import { loadS10Filters, type S10Filters } from './tabs/filters-state';

type TabKey = 'activity' | 'subscriptions' | 'filters';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'activity', label: 'Activity' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'filters', label: 'Filters' },
];

/**
 * S10 Inbox shell. Three tabs:
 *   - Activity (default): existing alert/mention/subscription rows
 *   - Subscriptions: per-user CRUD over /v1/users/me/subscriptions
 *   - Filters: localStorage-persisted unread + kind predicates
 *     applied to the Activity list.
 */
export function S10Inbox() {
  const [tab, setTab] = useState<TabKey>('activity');
  const [filters, setFilters] = useState<S10Filters>(() => loadS10Filters());

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-sp-4">
      <header className="flex items-baseline justify-between">
        <h1 className="text-fs-h1 font-medium text-gray-900">Inbox</h1>
      </header>
      <nav className="flex items-center gap-sp-2 border-b border-gray-300">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={
                'px-sp-3 py-sp-2 text-fs-body font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
                (active
                  ? 'border-b-2 border-gray-900 text-gray-900'
                  : 'text-gray-700 hover:text-gray-900')
              }
            >
              {t.label}
            </button>
          );
        })}
      </nav>
      <div role="tabpanel">
        {tab === 'activity' ? <ActivityTab filters={filters} /> : null}
        {tab === 'subscriptions' ? <SubscriptionsTab /> : null}
        {tab === 'filters' ? (
          <FiltersTab filters={filters} onChange={setFilters} />
        ) : null}
      </div>
    </div>
  );
}
